import { useState } from 'react';
import { ArrowRight, Twitter, Shield, Star, User, ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { BroUser } from './types';
import type { JobBoardAccess } from '@/hooks/useJobBoardAccess';
import { linkTwitterHandle } from '@/lib/engagement/twitter';

interface VerificationStep {
  id: string;
  title: string;
  description: string;
  action: string;
  completed: boolean;
}

interface VerificationPromptProps {
  user: BroUser;
  access: JobBoardAccess;
  requiredLevel: 'browse' | 'apply' | 'post';
  verificationSteps: VerificationStep[];
  onVerificationComplete?: () => void;
}

const VerificationPrompt: React.FC<VerificationPromptProps> = ({
  user,
  access,
  requiredLevel,
  onVerificationComplete
}) => {
  const [isTwitterDialogOpen, setIsTwitterDialogOpen] = useState(false);
  const [twitterHandle, setTwitterHandle] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');

  const handleTwitterVerification = async () => {
    if (!twitterHandle.trim()) {
      setVerificationMessage('Please enter your Twitter handle');
      return;
    }

    setIsVerifying(true);
    setVerificationMessage('');

    try {
      const result = await linkTwitterHandle(twitterHandle.trim());
      
      if (result.success) {
        setVerificationMessage('Twitter handle verified successfully!');
        setIsTwitterDialogOpen(false);
        onVerificationComplete?.();
      } else {
        setVerificationMessage(result.error || 'Failed to verify Twitter handle');
      }
    } catch (error) {
      setVerificationMessage('An error occurred during verification');
    } finally {
      setIsVerifying(false);
    }
  };

  const getNextStepAction = () => {
    if (!access.hasValidBroCode) {
      return {
        id: 'brocode',
        title: 'Connect BroCode',
        description: 'Link your BroCode to start your verification journey',
        icon: <User className="text-tan" size={20} />,
        action: () => window.location.href = '/initiation',
        buttonText: 'Connect BroCode',
        urgent: true
      };
    }

    if (!access.hasTwitterHandle) {
      return {
        id: 'twitter',
        title: 'Verify Twitter',
        description: 'Link your Twitter account to build trust and credibility',
        icon: <Twitter className="text-blue-400" size={20} />,
        action: () => setIsTwitterDialogOpen(true),
        buttonText: 'Verify Twitter',
        urgent: true
      };
    }

    if (!access.isVerified) {
      return {
        id: 'telegram',
        title: 'Complete Telegram Verification',
        description: 'Verify your Telegram account for secure communications',
        icon: <Shield className="text-green-400" size={20} />,
        action: () => window.open('https://t.me/BroMentumBot', '_blank'),
        buttonText: 'Verify on Telegram',
        urgent: true
      };
    }

    if (!access.hasMinimumScore) {
      return {
        id: 'broscore',
        title: 'Earn More BroScore',
        description: `You need ${25 - (user.bro_score || 0)} more BroScore to apply to jobs`,
        icon: <Star className="text-yellow-400" size={20} />,
        action: () => window.location.href = '/mini-app/engagement',
        buttonText: 'Earn BroScore',
        urgent: false
      };
    }

    if (requiredLevel === 'post' && !access.canPostJobs) {
      return {
        id: 'post-requirements',
        title: 'Unlock Job Posting',
        description: 'Increase your BroScore to 100+ to post jobs',
        icon: <Star className="text-purple-400" size={20} />,
        action: () => window.location.href = '/mini-app/engagement',
        buttonText: 'Earn More BroScore',
        urgent: false
      };
    }

    return null;
  };

  const nextStep = getNextStepAction();

  if (!nextStep) {
    return (
      <Card className="bg-green-500/10 border-green-500/30">
        <CardContent className="p-4">
          <div className="flex items-center space-x-3">
            <Shield className="text-green-400" size={24} />
            <div>
              <div className="text-green-400 font-medium">Verification Complete!</div>
              <div className="text-green-300 text-sm">
                You have full access to the job board features.
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className={`${nextStep.urgent ? 'bg-orange-500/10 border-orange-500/30' : 'bg-blue-500/10 border-blue-500/30'}`}>
        <CardHeader>
          <CardTitle className="text-tan flex items-center">
            {nextStep.icon}
            <span className="ml-2">Next Step: {nextStep.title}</span>
          </CardTitle>
          <CardDescription className="text-tan-dark">
            {nextStep.description}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="text-sm text-tan-dark">
              {nextStep.urgent ? 'Required to continue' : 'Recommended for better access'}
            </div>
            <Button
              onClick={nextStep.action}
              className={`${nextStep.urgent ? 'bg-orange-500 hover:bg-orange-600' : 'bg-tan hover:bg-tan-dark'} text-white`}
            >
              {nextStep.buttonText}
              <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions for Common Tasks */}
      <Card className="bg-navy-light border-tan-dark">
        <CardHeader>
          <CardTitle className="text-tan text-sm">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            {!access.hasTwitterHandle && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsTwitterDialogOpen(true)}
                className="border-tan-dark text-tan hover:bg-tan hover:text-navy"
              >
                <Twitter size={16} className="mr-2" />
                Link Twitter
              </Button>
            )}
            
            {!access.hasMinimumScore && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.location.href = '/mini-app/engagement'}
                className="border-tan-dark text-tan hover:bg-tan hover:text-navy"
              >
                <Star size={16} className="mr-2" />
                Earn BroScore
              </Button>
            )}
            
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open('https://t.me/BroMentumBot', '_blank')}
              className="border-tan-dark text-tan hover:bg-tan hover:text-navy"
            >
              <ExternalLink size={16} className="mr-2" />
              Telegram Bot
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Help and Support */}
      <Card className="bg-navy-light border-tan-dark">
        <CardHeader>
          <CardTitle className="text-tan text-sm">Need Help?</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm text-tan-dark">
            <div>• Join our Telegram community for support</div>
            <div>• Check the FAQ section for common questions</div>
            <div>• Contact support through the help center</div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open('https://t.me/BroMentumCommunity', '_blank')}
            className="mt-3 border-tan-dark text-tan hover:bg-tan hover:text-navy"
          >
            <ExternalLink size={16} className="mr-2" />
            Get Help
          </Button>
        </CardContent>
      </Card>

      {/* Twitter Verification Dialog */}
      <Dialog open={isTwitterDialogOpen} onOpenChange={setIsTwitterDialogOpen}>
        <DialogContent className="bg-navy border-tan-dark">
          <DialogHeader>
            <DialogTitle className="text-tan flex items-center">
              <Twitter className="mr-2 text-blue-400" size={20} />
              Verify Twitter Account
            </DialogTitle>
            <DialogDescription className="text-tan-dark">
              Link your Twitter account to increase your credibility and unlock job board features.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="twitter-handle" className="text-tan">
                Twitter Handle
              </Label>
              <Input
                id="twitter-handle"
                placeholder="Enter your Twitter username (without @)"
                value={twitterHandle}
                onChange={(e) => setTwitterHandle(e.target.value)}
                className="mt-1 bg-navy border-tan-dark text-tan placeholder-tan-dark"
                disabled={isVerifying}
              />
            </div>

            {verificationMessage && (
              <Alert>
                <AlertDescription className="text-tan-dark">
                  {verificationMessage}
                </AlertDescription>
              </Alert>
            )}

            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                onClick={() => setIsTwitterDialogOpen(false)}
                disabled={isVerifying}
                className="border-tan-dark text-tan hover:bg-tan hover:text-navy"
              >
                Cancel
              </Button>
              <Button
                onClick={handleTwitterVerification}
                disabled={isVerifying || !twitterHandle.trim()}
                className="bg-blue-500 hover:bg-blue-600 text-white"
              >
                {isVerifying ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={16} />
                    Verifying...
                  </>
                ) : (
                  <>
                    <Twitter className="mr-2" size={16} />
                    Verify Account
                  </>
                )}
              </Button>
            </div>

            <div className="text-xs text-tan-dark bg-navy rounded p-2 border border-tan-dark">
              <strong>Note:</strong> We use simplified Twitter verification for development. 
              In production, this would require posting a verification tweet.
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VerificationPrompt;